import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import getGuestCart from '../service/getGuestCart';
import {
  GuestCartContextType,
  GuestCartItem,
  GuestCartResponse,
  AddGuestCartResponse,
} from '../interface/interface';
import { useMutation } from '@tanstack/react-query';
import showToast from '../components/ToastComponent';
import removeGuestProductFromCart from '../service/removeGuestProductFromCart';
import addGuestProductToCart from '../service/addGuestProductCart';

const GuestCartContext = createContext<GuestCartContextType | undefined>(
  undefined,
);

export const useGuestCart = () => {
  const context = useContext(GuestCartContext);
  if (!context) {
    throw new Error('useGuestCart must be used within a GuestCartProvider');
  }
  return context;
};

interface GuestCartProviderProps {
  children: ReactNode;
}

export const GuestCartProvider: React.FC<GuestCartProviderProps> = ({
  children,
}) => {
  const [cartItems, setCartItems] = useState<GuestCartItem[]>([]);
  const [cartCount, setCartCount] = useState<number>(0);

  const refreshCart = async () => {
    try {
      // Check if cart ID exists first
      const guestCartId = sessionStorage.getItem('guestCartId');
      if (!guestCartId) {
        setCartItems([]);
        setCartCount(0);
        return;
      }

      const cartResponse: GuestCartResponse = await getGuestCart();

      if (cartResponse?.success && cartResponse.cart?.line_items) {
        const items: GuestCartItem[] = cartResponse.cart.line_items.map(
          (item: any) => ({
            product_id: item.product_id,
            quantity: item.quantity || 0,
          }),
        );

        setCartItems(items);

        setCartCount(items.reduce((total, item) => total + item.quantity, 0));

        // Update sessionStorage with current cart items for FloatingCart to use
        sessionStorage.setItem('guestCart', JSON.stringify(items));
      } else {
        setCartItems([]);
        setCartCount(0);
        sessionStorage.removeItem('guestCart');
      }
    } catch (error) {
      console.error('Error refreshing guest cart:', error);
      setCartItems([]);
      setCartCount(0);
      sessionStorage.removeItem('guestCart');
    }
  };

  const getProductQuantity = (productId: string): number => {
    const item = cartItems.find((item) => item.product_id === productId);
    return item ? item.quantity : 0;
  };

  const addToCartMutation = useMutation({
    mutationFn: async (productId: string) => {
      const result: AddGuestCartResponse =
        await addGuestProductToCart(productId);
      if (result?.success) {
        showToast({
          message: 'Item added to your cart.',
          type: 'success',
        });
        await refreshCart();
      }

      if (result?.status === 405 || result?.data?.message) {
        showToast({
          message: result.data?.message || 'Operation not allowed',
          type: 'warning',
        });
      }
      return result;
    },
    mutationKey: ['addToGuestCart'],
  });

  const removeProductFromCartMutation = useMutation({
    mutationKey: ['removeGuestCartItem'],
    mutationFn: async (productId: string) => {
      const result = await removeGuestProductFromCart(productId);
      if (result?.success) {
        showToast({
          message: 'Item removed from your cart.',
          type: 'success',
        });
      }
      return result;
    },
  });

  useEffect(() => {
    refreshCart();
  }, []);

  const value: GuestCartContextType = {
    cartItems,
    cartCount,
    refreshCart,
    getProductQuantity,
    addToCartMutation,
    removeProductFromCartMutation,
  };

  return (
    <GuestCartContext.Provider value={value}>
      {children}
    </GuestCartContext.Provider>
  );
};
